import usePendingContractorAudits from "../pendingContractors/usePendingContractorAudits";
import { useContext, useEffect } from "react";
import AuthContext from "../../components/context/AuthContext";
import { useNavigate } from "react-router-dom";
import Dropdown from "../../components/ui/dropdowns/Dropdown";
import Form from "../../components/ui/input/Form";
import SectionHeader from "../../components/ui/labels/SectionHeader";
import AuditContractorCard from "../../components/ui/containers/AuditContractorCard";
import axios from "axios";
import InfoPrompt from "../../components/ui/prompts/InfoPrompt";
import FullScreenActivityIndicator from "../../components/ui/activityIndicator/FullScreenActivityIndicator";
import TextArea from "../../components/ui/input/TextArea";
import Button from "../../components/ui/buttons/Button";
import Switch from "../../components/ui/switch/Switch";
import useGroupMemberships from "../../hooks/useGroupMemberships";
import DevModeLabel from "../../components/ui/labels/DevModeLabel";
import AuditData from "./AuditData";
import SplicerToolListCard from "../../components/ui/containers/SplicerToolListCard";

const PendingSplicerAudits = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    state,
    convertPendingAuditsHandler,
    toggleFetchingQuestionData,
    setQuestionData,
    setStatus,
    auditSelectHandler,
    commentsChangeHandler,
    submitReviewHandler,
    togglePassFail,
    resetForm,
  } = usePendingContractorAudits();

  const groupMemberships = useGroupMemberships();

  useEffect(() => {
    let isAdmin = groupMemberships.isPrimary || groupMemberships.isAuditPrimary;
    if (!isAdmin)
      navigate("/home", {
        replace: true,
      });
  }, [groupMemberships, authCtx, navigate]);

  useEffect(() => {
    if (!state.selectedAudit.value) return;

    toggleFetchingQuestionData();

    // Clean up async useEffect flag.
    let isSubscribed = true;
    let url = `${process.env.REACT_APP_API_URL}/audit/auditQuestionDetails`;
    let bodyObject = { auditGUID: state.selectedAudit.value.AUDIT_GUID };
    let authObj = { headers: { Authorization: `token ${authCtx.token}` } };
    axios
      .post(url, bodyObject, authObj)
      .then((response) => {
        if (isSubscribed) {
          response?.data
            ? setQuestionData(response.data)
            : setStatus("No question data found.");
        }
      })
      .catch((error) => {
        if (isSubscribed) {
          setStatus(
            error?.response?.data
              ? `Error: ${error.response.data}`
              : error?.message
              ? `Error: ${error.message}`
              : `Error: ${error}`
          );
          toggleFetchingQuestionData();
        }
      });

    // Cancel subscription to useEffect.
    return () => (isSubscribed = false);
    // eslint-disable-next-line
  }, [state.selectedAudit.value, state.fetchAudits]);

  return (
    <>
      <SectionHeader text={"Pending Splicer Audits"} />
      <DevModeLabel />
      <Form>
        <Dropdown
          identifier="pendingSplicerAudits"
          title="Pending Audits"
          url={`${process.env.REACT_APP_API_URL}/audit/pendingSplicerAudits`}
          fetchingMessage="Fetching Pending Audits..."
          noOptionsMessage="No pending audits found."
          value={state.selectedAudit}
          selectHandler={auditSelectHandler}
          convertOptionsHandler={convertPendingAuditsHandler}
          dependencies={[state.fetchAudits]}
        />
      </Form>
      {state.selectedAudit.value && (
        <AuditContractorCard contractorAudit={state.selectedAudit.value} />
      )}

      {!state.fetchingQuestionData && state.questionData && (
        <>
          <AuditData questionData={state.questionData} />
          <SectionHeader text="Tool List" overrideFontSize={"24px"} />
          {state.selectedAudit.value?.toolList && (
            <>
              <SplicerToolListCard
                toolList={state.selectedAudit.value.toolList}
              />
            </>
          )}
          {state.selectedAudit.value?.toolList === null && (
            <SectionHeader text="N/A" />
          )}
          <Form>
            <Switch
              label={state.passFail ? `Pass` : `Fail`}
              identifier={"condition"}
              isChecked={state.passFail}
              setIsChecked={() => togglePassFail(state.passFail ? false : true)}
            />
            <TextArea
              label={"Review Notes"}
              placeholder={"Notes"}
              identifier={"reviewNotes"}
              onChange={commentsChangeHandler}
              value={state.comments}
              required={true}
            />
            <Button
              disabled={!state.comments}
              onClick={submitReviewHandler}
              text="Submit Review"
            />
          </Form>
        </>
      )}
      {state.status && (
        <InfoPrompt text={state.status} statusCleared={resetForm} />
      )}
      <FullScreenActivityIndicator
        loading={state.fetchingQuestionData}
        text="Fetching Question Data..."
      />
      <FullScreenActivityIndicator
        loading={state.loading}
        text="Submitting Review..."
      />
    </>
  );
};

export default PendingSplicerAudits;
